import React, { useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiX } from "react-icons/fi";
import { get as commsGet, put as commsPut } from "../../../service/comms/commsApi";
import { getRoutingRules, putRoutingRules } from "../../../service/comms/supportService";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { useAgentsWorkload } from "./useSupportVocabulary";
import { toList } from "./supportUtils";
import { DEPARTMENTS } from "./supportEnums";

/** The only strategy the backend implements. */
const STRATEGY = "round_robin";

/** The company catch-all rule is the one with no department. */
const CATCH_ALL = { key: "", label: "Everything else", department: null };

const POOLS = [
  ...DEPARTMENTS.map((d) => ({ key: d, label: d, department: d })),
  CATCH_ALL,
];

/**
 * Why round-robin would skip an agent right now, or null if it wouldn't.
 *
 * The router picks the next `available` agent under `capacity`; everyone else
 * is passed over silently, which is exactly the thing a manager needs to see
 * before wondering why one person is getting every ticket.
 *
 * @param {Object} agent - Normalized agent.
 * @returns {string|null}
 */
const skipReason = (agent) => {
  if (agent.availability === "away") return "Away";
  if (agent.availability === "offline") return "Offline";
  if (agent.overloaded) {
    return agent.capacity ? `At capacity (${agent.openCount}/${agent.capacity})` : "At capacity";
  }
  return null;
};

/** One agent line inside a pool. */
const PoolAgent = ({ agent, index }) => {
  const skipped = skipReason(agent);
  return (
    <li className={`flex items-center gap-2 py-1 ${skipped ? "opacity-55" : ""}`}>
      <AvatarRing name={agent.name} colorIndex={index % 8} size="xs" />
      <span className="min-w-0 flex-1 truncate text-xs text-gray-700">{agent.name}</span>
      {skipped ? (
        <span className="shrink-0 rounded bg-gray-100 px-1.5 py-px text-[10px] font-semibold text-gray-500">
          {skipped}
        </span>
      ) : (
        <span className="shrink-0 font-mono text-[10px] text-gray-500">
          {agent.capacity ? `${agent.openCount}/${agent.capacity}` : agent.openCount} open
        </span>
      )}
    </li>
  );
};

/**
 * Auto-assign configuration: which queues route themselves, and who is in each
 * pool.
 *
 * Two halves, and they are governed by different things:
 *
 *  - **Routing is a manager setting.** `PUT /support/routing-rules` per
 *    `(company, department)`, with a NULL department as the catch-all.
 *  - **Pool membership is the agent's own.** `departments` and `capacity` live
 *    on their presence row, and `PUT /agents/me/presence` only ever writes the
 *    caller's — so agents claim their queues from `AgentPresenceToggle`, and
 *    this dialog shows the result rather than editing it. A picker here that
 *    silently did nothing would be worse than saying where it lives.
 *
 * Both halves are required, which is the failure worth designing for: a queue
 * switched on with an empty pool looks configured and routes nothing.
 *
 * @param {Object} props
 * @param {string|null} props.companyId
 * @param {() => void} props.onClose
 * @param {(message: string, type?: string) => void} [props.onFeedback]
 */
export default function AutoAssignDialog({ companyId, onClose, onFeedback }) {
  const [rules, setRules] = useState([]);
  // Already in the shared cache from the views rail's team strip — opening
  // this dialog paints the pools instantly rather than re-fetching them.
  const { agents } = useAgentsWorkload();
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let active = true;
    getRoutingRules(commsGet)
      .then((payload) => {
        if (active) setRules(toList(payload, ["rules", "items", "data"]));
      })
      // Manager tier — an agent can look at the pools but not change them.
      .catch(() => active && setForbidden(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onEscape = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEscape);
      document.body.style.overflow = "auto";
    };
  }, [onClose]);

  const ruleKey = (department) => department || "";
  const isEnabled = (department) =>
    rules.some((r) => ruleKey(r.department) === ruleKey(department) && r.is_enabled === true);

  /** Agents this pool would consider — the catch-all considers everyone. */
  const poolAgents = (department) =>
    department
      ? agents.filter((a) => (a.departments || []).includes(department))
      : agents;

  const toggle = async (department, enabled) => {
    const key = ruleKey(department);
    setSavingKey(key);
    // Optimistic: the switch should move under the finger.
    setRules((prev) => [
      ...prev.filter((r) => ruleKey(r.department) !== key),
      { company_id: companyId, department, is_enabled: enabled, strategy: STRATEGY },
    ]);
    try {
      await putRoutingRules(commsPut, {
        company_id: companyId,
        department,
        is_enabled: enabled,
        strategy: STRATEGY,
      });
      onFeedback?.(
        `Auto-assign ${enabled ? "on" : "off"} for ${department || "everything else"}.`
      );
    } catch (err) {
      console.error("Failed to save routing rule:", err);
      setRules((prev) =>
        prev.map((r) => (ruleKey(r.department) === key ? { ...r, is_enabled: !enabled } : r))
      );
      onFeedback?.(err?.message || "Could not save that routing rule.", "error");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Auto-assign"
        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl"
      >
        <div className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-gray-200 p-5 pb-4">
          <div>
            <h2 className="m-0 text-base font-bold text-gray-800">Auto-assign</h2>
            <p className="m-0 mt-1 text-xs text-gray-500">
              A new ticket goes to the next available agent in its pool who is under
              capacity. If nobody is eligible it waits in the pool rather than piling
              onto someone.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5 pt-4">
          {loading ? (
            <p className="m-0 text-xs text-gray-400">Loading routing rules…</p>
          ) : (
            <ul className="space-y-4">
              {POOLS.map((pool) => {
                const enabled = isEnabled(pool.department);
                const members = poolAgents(pool.department);
                const eligible = members.filter((a) => !skipReason(a));
                return (
                  <li key={pool.key} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="m-0 text-xs font-bold text-gray-800">{pool.label}</p>
                        <p className="m-0 mt-0.5 text-[11px] text-gray-500">
                          {members.length === 0
                            ? "Nobody takes this queue yet"
                            : `${eligible.length} of ${members.length} available now`}
                        </p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        aria-label={`Auto-assign for ${pool.label}`}
                        disabled={forbidden || savingKey === pool.key}
                        onClick={() => toggle(pool.department, !enabled)}
                        className={`inline-flex h-[18px] w-8 flex-shrink-0 items-center rounded-full p-0.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          enabled ? "justify-end bg-orange-primary" : "justify-start bg-gray-300"
                        }`}
                      >
                        <span className="h-3.5 w-3.5 rounded-full bg-white" />
                      </button>
                    </div>

                    {members.length > 0 && (
                      <ul className="mt-2 border-t border-gray-100 pt-1.5">
                        {members.map((agent, i) => (
                          <PoolAgent key={agent.userId} agent={agent} index={i} />
                        ))}
                      </ul>
                    )}

                    {/* Turning a pool on with nobody in it is the quiet way to
                        think auto-assign is working when it is doing nothing. */}
                    {enabled && members.length === 0 && (
                      <p className="m-0 mt-2 flex items-start gap-1.5 rounded bg-internal-bg px-2 py-1.5 text-[11px] text-internal-text">
                        <FiAlertTriangle aria-hidden="true" className="mt-0.5 shrink-0" />
                        On, but nobody covers this queue — so nothing routes. Agents
                        pick their queues from their own availability menu in the top
                        bar.
                      </p>
                    )}

                    {enabled && eligible.length === 0 && members.length > 0 && (
                      <p className="m-0 mt-2 flex items-start gap-1.5 rounded bg-internal-bg px-2 py-1.5 text-[11px] text-internal-text">
                        <FiAlertTriangle aria-hidden="true" className="mt-0.5 shrink-0" />
                        Everyone here is away or at capacity, so tickets will queue
                        unassigned until someone frees up.
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 px-5 py-3">
          <p className="m-0 text-[11px] leading-relaxed text-gray-500">
            {forbidden
              ? "Read-only — changing routing rules is a manager action."
              : "Two halves: you switch a queue on here, and each agent picks the queues they take from their own availability menu in the top bar. A queue routes only when it has both."}
          </p>
        </div>
      </div>
    </div>
  );
}
